import React, { useEffect, useMemo, useRef, useState } from "react";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useMap } from "react-leaflet";
import L from "leaflet";
import InfoCardFrame from "@/components/map/InfoCardFrame";

/**
 * A floating card anchored to a map lat/lng position.
 * Survives map drags, supports dragging the card itself, and blocks
 * map interaction when hovered.
 *
 * @param {object} props
 * @param {{lat: number, lng: number}} props.anchorLatLng - Map position to anchor to
 * @param {function} props.onClose - Called when close button is clicked
 * @param {string} props.title - Card header title
 * @param {string} [props.subtitle] - Optional subtitle below title
 * @param {React.ReactNode} [props.icon] - Optional icon in header
 * @param {number} [props.width=280] - Card width
 * @param {React.ReactNode} [props.actions] - Extra header buttons before drag/close
 * @param {React.ReactNode} props.children - Card body content
 */
export default function MapInfoCard({
  anchorLatLng,
  onClose,
  title,
  subtitle,
  icon,
  width = 280,
  headerColor,
  actions = null,
  children,
}) {
  const map = useMap();
  const cardRef = useRef(null);
  const draggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 16, y: -16 });
  const [viewportTick, setViewportTick] = useState(0);

  // Update position on map move/zoom
  useEffect(() => {
    if (!map) return;
    const onMove = () => setViewportTick((t) => t + 1);
    map.on("move", onMove);
    map.on("zoom", onMove);
    return () => {
      map.off("move", onMove);
      map.off("zoom", onMove);
    };
  }, [map]);

  // Block map interaction when hovering over card
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    L.DomEvent.disableClickPropagation(el);
    L.DomEvent.disableScrollPropagation(el);
  }, [title, anchorLatLng]);

  // Reset drag offset when anchor changes
  const anchorKey = `${anchorLatLng?.lat ?? "na"}-${anchorLatLng?.lng ?? "na"}`;
  useEffect(() => {
    setDragOffset({ x: 16, y: -16 });
  }, [anchorKey]);

  // Compute screen position from lat/lng
  const cardStyle = useMemo(() => {
    const lat = Number(anchorLatLng?.lat);
    const lng = Number(anchorLatLng?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return { top: 74, right: 8 };
    }
    const pt = map.latLngToContainerPoint([lat, lng]);
    return {
      left: pt.x + dragOffset.x,
      top: pt.y + dragOffset.y,
    };
  }, [map, anchorLatLng?.lat, anchorLatLng?.lng, dragOffset, viewportTick]);

  const headerActions = (
    <>
      {actions}
      <IconButton
        size="small"
        sx={{ color: "#004f7d", cursor: "grab", pointerEvents: "auto" }}
        onPointerDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
          e.currentTarget.setPointerCapture?.(e.pointerId);
          draggingRef.current = true;
          map?.dragging?.disable?.();
          dragStartRef.current = {
            x: dragOffset.x - e.clientX,
            y: dragOffset.y - e.clientY,
          };
        }}
        onPointerMove={(e) => {
          if (!draggingRef.current) return;
          e.stopPropagation();
          setDragOffset({
            x: dragStartRef.current.x + e.clientX,
            y: dragStartRef.current.y + e.clientY,
          });
        }}
        onPointerUp={(e) => {
          e.stopPropagation();
          draggingRef.current = false;
          map?.dragging?.enable?.();
          e.currentTarget.releasePointerCapture?.(e.pointerId);
        }}
        onPointerCancel={(e) => {
          e.stopPropagation();
          draggingRef.current = false;
          map?.dragging?.enable?.();
          e.currentTarget.releasePointerCapture?.(e.pointerId);
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={onClose}
        sx={{ color: "#004f7d", pointerEvents: "auto" }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  return (
    <InfoCardFrame
      ref={cardRef}
      title={title}
      subtitle={subtitle}
      icon={icon}
      width={width}
      headerColor={headerColor}
      headerActions={headerActions}
      sx={{
        position: "absolute",
        zIndex: 950,
        pointerEvents: "auto",
        ...cardStyle,
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerMove={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      {children}
    </InfoCardFrame>
  );
}
